/**
 * RangeFilter Pro – Shopify Collection Page Range Sliders
 * Version: 6.1.0 (fixed)  |  thesafehavens.co.uk
 *
 * Fixes from v6.0.0:
 *   1. callDawnRender now passes (searchParams, undefined) — Dawn's renderFilters
 *      safely skips the event-matching branch when event is undefined/null.
 *   2. Removed reference to undefined global `debounce` in _patchDawn.
 *   3. buildSearchParams always re-queries the live DOM form element.
 *   4. renderFilters patch properly preserves slider nodes without race conditions.
 *   5. MutationObserver rebuild is more defensive against infinite loops.
 */
(function () {
  'use strict';

  const CONFIG = { currencySymbol: '£', debounceMs: 150 };
  const deb   = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
  const getParams = () => new URLSearchParams(window.location.search);

  // ── Price bounds ─────────────────────────────────────────────────────────────
  const getPriceBounds = () => {
    const pp = [];
    document.querySelectorAll('[data-price]').forEach(e => {
      const v = parseInt(e.dataset.price); if (v > 0) pp.push(v);
    });
    document.querySelectorAll('.price__regular .price-item, .price .money').forEach(e => {
      const v = Math.round(parseFloat(e.textContent.replace(/[^0-9.]/g, '')) * 100);
      if (v > 0) pp.push(v);
    });
    document.querySelectorAll('details.js-filter').forEach(d => {
      if (d.querySelector('summary')?.textContent?.trim().toLowerCase() !== 'price') return;
      const m = d.textContent.match(/[\d,]+/g);
      if (m) m.forEach(s => { const v = Math.round(parseFloat(s.replace(/,/g, '')) * 100); if (v > 0) pp.push(v); });
    });
    return pp.length >= 2 ? { min: Math.min(...pp), max: Math.max(...pp) } : { min: 0, max: 1535000 };
  };

  // ── FIX #1: Call Dawn's renderPage correctly ─────────────────────────────────
  // Dawn signature: static renderPage(searchParams, event, updateURLHash = true)
  // When event is null/undefined, renderFilters skips the "matchesId" branch
  // and re-renders ALL facets — which is exactly what we want.
  const callDawnRender = deb((searchParams) => {
    if (window.FacetFiltersForm && typeof FacetFiltersForm.renderPage === 'function') {
      try {
        // Pass undefined for event so renderFilters treats all facets as "to render"
        // Pass true for updateURLHash so the URL updates
        FacetFiltersForm.renderPage(searchParams, undefined, true);
      } catch (err) {
        console.warn('[RangeFilterPro] Dawn renderPage failed, falling back to navigation:', err);
        window.location.search = searchParams;
      }
    } else {
      window.location.search = searchParams;
    }
  }, CONFIG.debounceMs);

  // ── FIX #3: Always get a fresh reference to the form ─────────────────────────
  const buildSearchParams = (updates) => {
    // Re-query the DOM every time — after Dawn re-renders, the old form is gone
    const form = document.querySelector('#FacetFiltersForm');
    let p;
    if (form) {
      try {
        p = new URLSearchParams(new FormData(form));
      } catch (e) {
        p = getParams();
      }
    } else {
      p = getParams();
    }
    p.delete('page');

    // Apply our slider updates on top
    for (const [key, val] of Object.entries(updates)) {
      if (val.type === 'price') {
        if (val.lo <= val.absMin && val.hi >= val.absMax) {
          p.delete('filter.v.price.gte'); p.delete('filter.v.price.lte');
        } else {
          p.set('filter.v.price.gte', (val.lo / 100).toFixed(2));
          p.set('filter.v.price.lte', (val.hi / 100).toFixed(2));
        }
      } else if (val.type === 'checkbox-range') {
        p.delete(key);
        if (val._isRaw) {
          val.allVals.forEach(v => p.append(key, String(v)));
        } else if (!(val.lo <= val.absMin && val.hi >= val.absMax)) {
          val.allVals.filter(v => v >= val.lo && v <= val.hi)
                     .forEach(v => p.append(key, String(v)));
        }
      } else if (val.type === 'numrange') {
        if (val.lo <= val.absMin && val.hi >= val.absMax) {
          p.delete(val.gteKey); p.delete(val.lteKey);
        } else {
          p.set(val.gteKey, String(val.lo));
          p.set(val.lteKey, String(val.hi));
        }
      }
    }
    return p.toString();
  };

  // ── Dual range slider (unchanged) ─────────────────────────────────────────────
  class Slider {
    constructor(host, { min, max, step = 1, valMin, valMax, isPrice, label, onChange }) {
      this.min = min; this.max = max; this.step = step;
      this.vMin = valMin ?? min; this.vMax = valMax ?? max;
      this.isPrice = isPrice; this.label = label || '';
      this.onChange = onChange;
      this._build(host); this._bind(); this._paint();
    }
    _toDisp(v) { return this.isPrice ? v / 100 : v; }
    _fromDisp(s) { const v = parseFloat(s); return this.isPrice ? Math.round(v * 100) : v; }
    _build(host) {
      host.innerHTML = `
        <div class="rf-sw" role="group" aria-label="${this.label} filter">
          <div class="rf-tw">
            <div class="rf-tr"><div class="rf-tf"></div></div>
            <input class="rf-r rf-lo" type="range" min="${this.min}" max="${this.max}" step="${this.step}" value="${this.vMin}" aria-label="Min ${this.label}">
            <input class="rf-r rf-hi" type="range" min="${this.min}" max="${this.max}" step="${this.step}" value="${this.vMax}" aria-label="Max ${this.label}">
          </div>
          <div class="rf-lbs">
            <div class="rf-lg">
              <span class="rf-lt">Min</span>
              <div class="rf-iw">${this.isPrice ? `<span class="rf-sym">${CONFIG.currencySymbol}</span>` : ''}
                <input class="rf-ni rf-lo-n" type="number" value="${this._toDisp(this.vMin)}" inputmode="decimal" aria-label="Min">
              </div>
            </div>
            <div class="rf-dash">–</div>
            <div class="rf-lg rf-lgr">
              <span class="rf-lt">Max</span>
              <div class="rf-iw">${this.isPrice ? `<span class="rf-sym">${CONFIG.currencySymbol}</span>` : ''}
                <input class="rf-ni rf-hi-n" type="number" value="${this._toDisp(this.vMax)}" inputmode="decimal" aria-label="Max">
              </div>
            </div>
          </div>
        </div>`;
      this.tf = host.querySelector('.rf-tf');
      this.rLo = host.querySelector('.rf-lo'); this.rHi = host.querySelector('.rf-hi');
      this.nLo = host.querySelector('.rf-lo-n'); this.nHi = host.querySelector('.rf-hi-n');
    }
    _paint() {
      const r = this.max - this.min;
      if (r <= 0) return;
      this.tf.style.left = ((this.vMin - this.min) / r * 100) + '%';
      this.tf.style.right = ((this.max - this.vMax) / r * 100) + '%';
      this.rLo.value = this.vMin; this.rHi.value = this.vMax;
      this.nLo.value = this._toDisp(this.vMin); this.nHi.value = this._toDisp(this.vMax);
    }
    _bind() {
      const fire = deb(() => this.onChange(this.vMin, this.vMax), CONFIG.debounceMs);
      const gap = this.isPrice ? 100 : this.step;
      this.rLo.addEventListener('input', e => {
        e.stopPropagation();
        const v = parseFloat(e.target.value);
        if (v >= this.vMax - gap) { e.target.value = this.vMax - gap; return; }
        this.vMin = v; this._paint(); fire();
      });
      this.rHi.addEventListener('input', e => {
        e.stopPropagation();
        const v = parseFloat(e.target.value);
        if (v <= this.vMin + gap) { e.target.value = this.vMin + gap; return; }
        this.vMax = v; this._paint(); fire();
      });
      const nf = deb((isLo, raw) => {
        const v = this._fromDisp(raw); if (isNaN(v)) return;
        if (isLo) this.vMin = clamp(v, this.min, this.vMax - gap);
        else      this.vMax = clamp(v, this.vMin + gap, this.max);
        this._paint(); this.onChange(this.vMin, this.vMax);
      }, 300);
      this.nLo.addEventListener('input', e => { e.stopPropagation(); nf(true, e.target.value); });
      this.nHi.addEventListener('input', e => { e.stopPropagation(); nf(false, e.target.value); });
      [this.rLo, this.rHi].forEach(r => {
        r.addEventListener('touchstart', () => { document.body.style.overscrollBehavior = 'none'; }, { passive: true });
        r.addEventListener('touchend', () => { document.body.style.overscrollBehavior = ''; }, { passive: true });
      });
    }
    set(lo, hi) { this.vMin = lo; this.vMax = hi; this._paint(); }
  }

  // ── Main controller ───────────────────────────────────────────────────────────
  class RFPro {
    constructor() {
      this._rebuilding = false;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._boot());
      } else {
        setTimeout(() => this._boot(), 80);
      }
    }

    _boot() {
      this._styles();
      this._patchDawn();
      this._buildAll();

      // FIX #5: More defensive MutationObserver
      new MutationObserver(deb(() => {
        if (this._rebuilding) return;
        const form = document.querySelector('#FacetFiltersForm');
        if (form && !form.querySelector('.rf-host')) {
          this._rebuilding = true;
          try {
            this._buildAll();
          } finally {
            this._rebuilding = false;
          }
        }
      }, 200)).observe(document.body, { childList: true, subtree: true });
    }

    // ── FIX #2 & #4: Simplified Dawn patch ──────────────────────────────────────
    _patchDawn() {
      const self = this;

      const tryPatch = () => {
        if (!window.FacetFiltersForm || FacetFiltersForm._rfPatched) return;
        FacetFiltersForm._rfPatched = true;

        const origRenderFilters = FacetFiltersForm.renderFilters;
        FacetFiltersForm.renderFilters = function (html, event) {
          // Save slider state before Dawn wipes innerHTML
          const savedSliders = new Map();
          document.querySelectorAll('.js-filter').forEach(jsf => {
            const hosts = [...jsf.querySelectorAll('.rf-host')];
            if (hosts.length) {
              // Detach from DOM so Dawn's innerHTML doesn't destroy them
              hosts.forEach(h => h.remove());
              savedSliders.set(jsf.id, hosts);
            }
          });

          // Let Dawn do its normal rendering
          origRenderFilters.call(this, html, event);

          // Re-inject saved slider nodes
          savedSliders.forEach((hosts, id) => {
            const el = document.getElementById(id);
            if (!el) return;
            hosts.forEach(host => {
              const target = el.querySelector(
                '.facets__display-vertical,.facets__display,[class*="facets__display"]'
              ) || el.querySelector('summary + *') || el;
              target.prepend(host);
            });
            // Re-hide the checkbox lists that Dawn restored
            el.querySelectorAll('ul,ol,.facets__list').forEach(l => {
              l.style.cssText = 'display:none!important';
            });
            el.querySelectorAll('li,.facets__item,.facets__show-more,button[class*="show-more"]').forEach(e => {
              e.style.cssText = 'display:none!important';
            });
            el.setAttribute('open', '');
          });

          // Re-hide price native UI
          document.querySelectorAll('price-range,.facets__price-label,[class*="price-label"]').forEach(el => {
            el.style.cssText = 'display:none!important';
          });
        };
      };

      tryPatch();
      setTimeout(tryPatch, 100);
      setTimeout(tryPatch, 600);
      setTimeout(tryPatch, 1500);
    }

    _buildAll() {
      this._buildPriceSlider();
      this._buildListSliders();
    }

    // ── Price slider ──────────────────────────────────────────────────────────
    _buildPriceSlider() {
      const pd = this._findDetails('price');
      if (!pd || pd.querySelector('.rf-host')) return;

      const bounds = getPriceBounds();
      const p = getParams();
      const curMin = p.has('filter.v.price.gte') ? Math.round(parseFloat(p.get('filter.v.price.gte')) * 100) : bounds.min;
      const curMax = p.has('filter.v.price.lte') ? Math.round(parseFloat(p.get('filter.v.price.lte')) * 100) : bounds.max;

      const host = document.createElement('div');
      host.className = 'rf-host';
      const target = pd.querySelector('.facets__display-vertical,.facets__display,[class*="facets__display"]')
                  || pd.querySelector('summary+*') || pd;
      target.prepend(host);

      new Slider(host, {
        min: bounds.min, max: bounds.max, step: 100,
        valMin: curMin, valMax: curMax, isPrice: true, label: 'Price',
        onChange: (lo, hi) => {
          const sp = buildSearchParams({
            '__price__': { type: 'price', lo, hi, absMin: bounds.min, absMax: bounds.max }
          });
          callDawnRender(sp);
        }
      });

      pd.querySelectorAll('price-range,.facets__price,.price-range').forEach(el => {
        el.style.cssText = 'display:none!important';
      });
      pd.querySelectorAll('[class*="price-label"],.facets__price-label').forEach(el => {
        el.style.cssText = 'display:none!important';
      });
      pd.setAttribute('open', '');
    }

    // ── Checkbox-list + .gte/.lte sliders ────────────────────────────────────
    _buildListSliders() {
      const form = document.querySelector('#FacetFiltersForm,form.facets__form-vertical,form.facets__form');
      if (!form) return;

      // Checkbox numeric lists
      form.querySelectorAll('details.js-filter,details[data-index]').forEach(details => {
        if (details.querySelector('.rf-host')) return;
        const label = details.querySelector('summary')?.textContent?.replace(/\(\d+\)/g, '').trim() || '';
        if (label.toLowerCase() === 'price') return;

        const checkboxes = [...details.querySelectorAll('input[type="checkbox"]')];
        if (!checkboxes.length) return;

        // Handle range-format checkbox values like "51-150", "151-250" etc.
        const hasRangeValues = checkboxes.some(cb => /^\d+-\d+$/.test(cb.value.trim()));
        if (hasRangeValues) {
          const rangeCbs = checkboxes.map(cb => {
            const m = cb.value.match(/^(\d+)-(\d+)$/);
            return m ? { lo: parseFloat(m[1]), hi: parseFloat(m[2]), cb } : null;
          }).filter(Boolean);
          if (rangeCbs.length < 2) return;

          const paramName2 = checkboxes[0].name;
          const absMin2 = Math.min(...rangeCbs.map(r => r.lo));
          const absMax2 = Math.max(...rangeCbs.map(r => r.hi));
          const p2 = getParams();
          const active2 = p2.getAll(paramName2);
          const activeRanges = rangeCbs.filter(r => active2.includes(r.cb.value));
          const curMin2 = activeRanges.length ? Math.min(...activeRanges.map(r => r.lo)) : absMin2;
          const curMax2 = activeRanges.length ? Math.max(...activeRanges.map(r => r.hi)) : absMax2;

          const host2 = document.createElement('div');
          host2.className = 'rf-host rf-host--inline';
          const target2b = details.querySelector('.facets__display-vertical,.facets__display,[class*="facets__display"]')
                       || details.querySelector('summary+*') || details;
          target2b.prepend(host2);

          new Slider(host2, {
            min: absMin2, max: absMax2, step: 1,
            valMin: curMin2, valMax: curMax2,
            isPrice: false, label,
            onChange: (lo, hi) => {
              const isAll = lo <= absMin2 && hi >= absMax2;
              const sp2 = buildSearchParams({
                [paramName2]: {
                  type: 'checkbox-range',
                  lo, hi, absMin: absMin2, absMax: absMax2,
                  allVals: rangeCbs
                    .filter(r => !isAll && r.hi >= lo && r.lo <= hi)
                    .map(r => r.cb.value),
                  _isRaw: true
                }
              });
              callDawnRender(sp2);
            }
          });

          details.querySelectorAll('ul,ol,.facets__list').forEach(l => {
            l.style.cssText = 'display:none!important';
          });
          details.querySelectorAll('li,.facets__item,.facets__show-more,button[class*="show-more"]').forEach(e => {
            e.style.cssText = 'display:none!important';
          });
          details.setAttribute('open', '');
          return;
        }

        const vals = checkboxes.map(cb => parseFloat(cb.value)).filter(v => !isNaN(v) && v >= 0);
        if (vals.length !== checkboxes.length || vals.length < 2) return;

        const paramName = checkboxes[0].name;
        const sorted = [...new Set(vals)].sort((a, b) => a - b);
        const absMin = sorted[0], absMax = sorted[sorted.length - 1];
        const p = getParams();
        const active = p.getAll(paramName).map(v => parseFloat(v)).filter(v => !isNaN(v));
        const curMin = active.length ? Math.min(...active) : absMin;
        const curMax = active.length ? Math.max(...active) : absMax;
        const step = Math.max(sorted.length > 1 ? sorted[1] - sorted[0] : 1, 1);

        const host = document.createElement('div');
        host.className = 'rf-host rf-host--inline';
        const target2 = details.querySelector('.facets__display-vertical,.facets__display,[class*="facets__display"]')
                     || details.querySelector('summary+*') || details;
        target2.prepend(host);

        new Slider(host, {
          min: absMin, max: absMax, step, valMin: curMin, valMax: curMax,
          isPrice: false, label,
          onChange: (lo, hi) => {
            const sp = buildSearchParams({
              [paramName]: { type: 'checkbox-range', lo, hi, absMin, absMax, allVals: sorted }
            });
            callDawnRender(sp);
          }
        });

        details.querySelectorAll('ul,ol,.facets__list').forEach(l => {
          l.style.cssText = 'display:none!important';
        });
        details.querySelectorAll('li,.facets__item,.facets__show-more,button[class*="show-more"]').forEach(e => {
          e.style.cssText = 'display:none!important';
        });
        details.setAttribute('open', '');
      });

      // .gte/.lte number input pairs
      form.querySelectorAll('input[type="number"][name*=".gte"]').forEach(gIn => {
        if (gIn.name.includes('price')) return;
        const key = gIn.name.replace(/\.gte$/, '');
        const lIn = form.querySelector(`input[name="${key}.lte"]`);
        if (!lIn) return;
        const wrap = gIn.closest('details,.js-filter') || gIn.parentElement;
        if (!wrap || wrap.querySelector('.rf-host')) return;

        const label = wrap.querySelector('summary,.facets__summary')?.textContent?.replace(/\(\d+\)/g, '').trim() || key.split('.').pop().replace(/_/g, ' ');
        const absMin = parseFloat(gIn.min || '0') || 0;
        const absMax = parseFloat(lIn.max || '0') || 10000;
        const p = getParams();
        const curMin = p.has(gIn.name) ? parseFloat(p.get(gIn.name)) : absMin;
        const curMax = p.has(lIn.name) ? parseFloat(p.get(lIn.name)) : absMax;
        const step = absMax > 5000 ? 50 : absMax > 500 ? 5 : 1;

        const host = document.createElement('div');
        host.className = 'rf-host rf-host--inline';
        const target3 = wrap.querySelector('.facets__display-vertical,.facets__display,[class*="facets__display"]')
                     || wrap.querySelector('summary+*') || wrap;
        target3.prepend(host);

        new Slider(host, {
          min: absMin, max: absMax, step, valMin: curMin, valMax: curMax,
          isPrice: false, label,
          onChange: (lo, hi) => {
            const sp = buildSearchParams({
              '__numrange__': { type: 'numrange', lo, hi, absMin, absMax, gteKey: gIn.name, lteKey: lIn.name }
            });
            callDawnRender(sp);
          }
        });

        [gIn, lIn].forEach(i => {
          i.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;';
        });
        if (wrap.tagName === 'DETAILS') {
          wrap.setAttribute('open', '');
          wrap.querySelectorAll('.facets__show-more,button[class*="show-more"],.show-more-item').forEach(e => {
            e.style.cssText = 'display:none!important';
          });
        }
      });
    }

    _findDetails(lbl) {
      for (const d of document.querySelectorAll('details.js-filter,details[data-index]')) {
        if (d.querySelector('summary')?.textContent?.trim().toLowerCase() === lbl) return d;
      }
      return document.querySelector('input[name*="price.gte"]')?.closest('details') || null;
    }

    _styles() {
      if (document.getElementById('rf-css')) return;
      const s = document.createElement('style');
      s.id = 'rf-css';
      s.textContent = `
        .rf-host--inline{padding:10px 0 4px;}
        .rf-tw{position:relative;height:30px;display:flex;align-items:center;margin-bottom:10px;}
        .rf-tr{position:absolute;left:0;right:0;height:4px;background:#e8e8e8;border-radius:2px;z-index:1;pointer-events:none;}
        .rf-tf{position:absolute;top:0;bottom:0;background:#111;border-radius:2px;transition:left .04s,right .04s;}
        .rf-r{position:absolute;left:0;width:100%;height:4px;background:transparent;-webkit-appearance:none;appearance:none;pointer-events:none;z-index:2;margin:0;padding:0;}
        .rf-r:focus{outline:none;}
        .rf-r::-webkit-slider-thumb{-webkit-appearance:none;width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #111;cursor:pointer;pointer-events:all;transition:transform .1s,box-shadow .1s;box-shadow:0 1px 4px rgba(0,0,0,.12);}
        .rf-r::-webkit-slider-thumb:hover,.rf-r::-webkit-slider-thumb:active{transform:scale(1.15);box-shadow:0 2px 8px rgba(0,0,0,.18);}
        .rf-r::-moz-range-thumb{width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #111;cursor:pointer;pointer-events:all;box-shadow:0 1px 4px rgba(0,0,0,.12);}
        .rf-r::-moz-range-track{background:transparent;}
        @media(max-width:768px){.rf-r::-webkit-slider-thumb,.rf-r::-moz-range-thumb{width:28px;height:28px;}}
        .rf-lbs{display:flex;align-items:center;gap:8px;}
        .rf-lg{flex:1;display:flex;flex-direction:column;gap:3px;}
        .rf-lgr{align-items:flex-end;}
        .rf-dash{color:#ccc;font-size:13px;flex-shrink:0;margin-top:14px;}
        .rf-lt{font-size:10px;color:#aaa;text-transform:uppercase;letter-spacing:.04em;}
        .rf-iw{display:flex;align-items:center;gap:2px;border:1px solid #e0e0e0;border-radius:5px;padding:5px 8px;background:#fafafa;transition:border-color .15s,background .15s;width:100%;}
        .rf-iw:focus-within{border-color:#111;background:#fff;}
        .rf-sym{font-size:12px;color:#aaa;flex-shrink:0;}
        .rf-ni{width:100%;border:none;background:transparent;font-size:13px;font-weight:500;color:#111;font-family:inherit;padding:0;-moz-appearance:textfield;}
        .rf-ni::-webkit-outer-spin-button,.rf-ni::-webkit-inner-spin-button{-webkit-appearance:none;}
        .rf-ni:focus{outline:none;}
        details:has(.rf-host) price-range,
        details:has(.rf-host) .facets__price,
        details:has(.rf-host) [class*="price-label"],
        details:has(.rf-host) .facets__price-label{display:none!important;}
        details:has(.rf-host--inline) ul,
        details:has(.rf-host--inline) ol,
        details:has(.rf-host--inline) li,
        details:has(.rf-host--inline) .facets__list,
        details:has(.rf-host--inline) .facets__item,
        details:has(.rf-host--inline) .facets__show-more,
        details:has(.rf-host--inline) button[class*="show-more"],
        details:has(.rf-host--inline) .show-more-item{display:none!important;}
        .active-facets,.active-facets__button,facet-remove,.facets__selected{display:none!important;}
        @media(prefers-color-scheme:dark){
          .rf-tr{background:#333;}.rf-tf{background:#e0e0e0;}
          .rf-r::-webkit-slider-thumb{background:#1a1a1a;border-color:#e0e0e0;}
          .rf-r::-moz-range-thumb{background:#1a1a1a;border-color:#e0e0e0;}
          .rf-iw{background:#222;border-color:#444;}.rf-iw:focus-within{border-color:#fff;background:#111;}
          .rf-ni{color:#f0f0f0;}
        }
      `;
      document.head.appendChild(s);
    }
  }

  window.RangeFilterPro = new RFPro();
})();
